import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Grid,
  Divider
} from "@material-ui/core";

import Widget from "../Widget";
import { Button, Typography } from "../Wrappers";
import config from "../../config";
import useStyles from "./styles";

export default function LastYearCheck() {
  const classes = useStyles();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  function formatDateYYYYMMDD(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const reportMonth = React.useMemo(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
    //return new Date(2021, 3, 1); // testing:  April 2021 (month is 0-based)
  }, []);

  const reportMonthStr = formatDateYYYYMMDD(reportMonth);

  useEffect(() => {
    let mounted = true;

    async function fetchData() {
      try {
        const response = await fetch(
          `${config.clientApi}/dashboard/last_year_check?month=${reportMonthStr}`,
          {
            credentials: "include"
          }
        );

        const json = await response.json();

        if (mounted) {
          setData(json);
          setLoading(false);
        }
      } catch (e) {
        console.error(e);
        if (mounted) {
          setData(null);
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => mounted = false;
  }, [reportMonthStr]);

  function downloadReport(franchiseId) {
    const url =
      `${config.clientApi}/report/missing_work?month=${reportMonthStr}&region_id=${franchiseId}`;

    window.open(url, "_blank");
  }

  function monthLabel() {
    return reportMonth.toLocaleDateString("en-NZ", {
      month: "short",
      year: "numeric"
    });
  }

  function getMainScopes(region) {
    return region.scopes.filter(scope =>
      scope.scope_name !== "Thermal"
    );
  }

  function getThermalScope(region) {
    return region.scopes.find(scope =>
      scope.scope_name === "Thermal"
    );
  }

  if (loading) {
    return (
      <Widget title="Based on the same month last year" disableWidgetMenu>
        <CircularProgress size={24} />
      </Widget>
    );
  }

  if (!data || !data.regions || data.regions.length === 0) {
    return (
      <Widget title="Based on the same month last year" disableWidgetMenu>
        <Typography color="text" colorBrightness="secondary">
          No potential missed work found for {monthLabel()}.
        </Typography>
      </Widget>
    );
  }

  return (
    <Widget
      title={`Based on the same month last year - ${monthLabel()}`}
      disableWidgetMenu
      showOverflow
    >
      <Typography
        size="sm"
        color="text"
        colorBrightness="secondary"
        className={classes.helperText}
      >
        Sites completed same month last year but not yet actioned this year.
      </Typography>

      {data.regions.map((region, index) => {
        const mainScopes = getMainScopes(region);
        const thermal = getThermalScope(region);

        return (
          <div key={region.franchise_id} className={classes.regionBlock}>
            {index > 0 && <Divider className={classes.divider} />}

            <div className={classes.regionHeader}>
              <div>
                <Typography variant="h6">
                  {region.franchise_name}
                </Typography>

                <Typography
                  size="sm"
                  color="text"
                  colorBrightness="secondary"
                >
                  {region.total_needs_attention} sites need attention
                  {region.total_potential_assets > 0
                    ? ` · ${region.total_potential_assets} potential assets`
                    : ""}
                </Typography>
              </div>

              <Button
                size="small"
                color="primary"
                variant="contained"
                onClick={() => downloadReport(region.franchise_id)}
              >
                Download report
              </Button>
            </div>

            <Grid container spacing={2} className={classes.scopeGrid}>
              {mainScopes.map(scope => (
                <Grid item xs={12} sm={6} md={4} key={scope.scope_name}>
                  <div className={classes.scopeBox}>
                    <Typography weight="medium">
                      {scope.scope_name}
                    </Typography>

                    <Typography size="sm">
                      {scope.not_actioned_count} need attention
                    </Typography>

                    {scope.show_assets && (
                      <Typography
                        size="sm"
                        color="text"
                        colorBrightness="secondary"
                      >
                        {scope.potential_assets} potential assets · {scope.site_count} sites
                      </Typography>
                    )}

                    {!scope.show_assets && (
                      <Typography
                        size="sm"
                        color="text"
                        colorBrightness="secondary"
                      >
                        {scope.site_count} sites
                      </Typography>
                    )}
                  </div>
                </Grid>
              ))}

              {thermal && (
                <Grid item xs={12} sm={6} md={4}>
                  <div className={classes.scopeBoxMuted}>
                    <Typography weight="medium">
                      Thermal
                    </Typography>

                    <Typography
                      size="sm"
                      color="text"
                      colorBrightness="secondary"
                    >
                      {thermal.site_count} sites shown separately
                    </Typography>
                  </div>
                </Grid>
              )}
            </Grid>
          </div>
        );
      })}
    </Widget>
  );
}
